import {Menu} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {parseCookies} from 'nookies';
import {type FC, type ReactNode, useState} from 'react';
import {
  MdAddCircleOutline,
  MdCheckCircle,
  MdControlPoint,
  MdDomain,
} from 'react-icons/md';
import {COLOR_LINK_1} from '../../../../config/constants/colorCodes';
import {validateCookie} from '../../../../core/usecases/auth/checkOauthTokenExistence';
import {useHandleFollowCompany} from '../../../../core/usecases/useHandleFollowCompany';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {NoLoginAlertModal} from '../../../../shared/components/NoLoginAlertModal';
import {buildCompanyFollowGtmContent} from '../../../../shared/services/buildCompanyFollowGtmContent';

const MENU_ICON_COLOR = COLOR_LINK_1;

type Props = {
  companyId: number;
  companyName: string;
  isFollowed: boolean;
  followerCount: number;
  children: ReactNode;
  onFollowSuccess?: () => void;
};

export const CompanyLogoMenu: FC<Props> = ({
  companyId,
  companyName,
  isFollowed,
  followerCount,
  children,
  onFollowSuccess,
}) => {
  const [opened, setOpened] = useState(false);
  const [loginAlertOpened, {open: openLoginAlert, close: closeLoginAlert}] =
    useDisclosure(false);

  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);

  const {
    handleFollowCompany,
    followCountUpdated,
    isFollowed: isFollowedState,
  } = useHandleFollowCompany({
    companyId,
    followCount: followerCount,
    followed: isFollowed,
  });

  const isDisabled = companyId === 0;

  const onClickFollow = async () => {
    setOpened(false);
    if (!isValid) {
      openLoginAlert();
      return;
    }
    const wasFollowed = isFollowedState;
    try {
      await handleFollowCompany();
      if (!wasFollowed) {
        onFollowSuccess?.();
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Menu
        position="bottom-start"
        withArrow
        opened={opened}
        onChange={setOpened}
        disabled={isDisabled}
      >
        <Menu.Target>
          <button
            type="button"
            className="cursor-pointer inline-block relative"
            onClick={() => {
              setOpened((prev) => !prev);
            }}
            aria-label="企業メニューを開く"
          >
            {children}
            <span className="absolute bottom-0 right-0 bg-white rounded-full leading-none">
              {isFollowedState ? (
                <MdCheckCircle size={18} color={MENU_ICON_COLOR} />
              ) : (
                <MdAddCircleOutline size={18} color={MENU_ICON_COLOR} />
              )}
            </span>
          </button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            icon={
              isFollowedState ? (
                <MdCheckCircle size={20} color={MENU_ICON_COLOR} />
              ) : (
                <MdControlPoint size={20} color={MENU_ICON_COLOR} />
              )
            }
            onClick={onClickFollow}
            data-gtm-click="company-follow"
            data-gtm-content={buildCompanyFollowGtmContent({
              companyId,
              companyName,
              location: 'recruiting-info-logo-menu',
            })}
            rightSection={
              <span className="text-sm text-black-2 ml-8">
                {followCountUpdated}
              </span>
            }
          >
            {isFollowedState ? 'フォロー中' : 'フォロー追加'}
          </Menu.Item>
          <Menu.Item
            icon={<MdDomain size={20} color={MENU_ICON_COLOR} />}
            component={CommonLink}
            href={`/company/${companyId}`}
            className="text-base pc:hover:opacity-80 leading"
            target="_blank"
            rel="noopener noreferrer"
          >
            企業ページへ
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <NoLoginAlertModal opened={loginAlertOpened} onClose={closeLoginAlert} />
    </>
  );
};
